import React, { useState, useEffect } from "react";
import useFormatTime from "@/hooks/useFormatTime";

import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
  IconButton,
  Tooltip,
  Pagination,
  Snackbar,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assessment as AnalyticsIcon,
  Assignment as AssignmentIcon,
  Quiz as ExamIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  ViewList as PartIcon,
  FitnessCenter as ExerciseIcon,
  MenuBook,
  BarChart,
  Home,
} from "@mui/icons-material";
import {
  useGetAnalyticsQuery,
  useGetUsersQuery,
  useUpdateUserRoleMutation,
  useDeleteUserMutation,
  useGetAssignmentSubmissionsQuery,
  useGetExamAttemptsQuery,
} from "@/store/api/adminApi";

import AssignmentManagement from "./AssignmentManagement";
import ExamManagement from "./ExamManagement";
import PartManagement from "./PartManagement";
import UserManagement from "./UserManagement";
import ExerciseManagement from "./ExerciseManagement";
import HomepageManagement from "./HomepageManagement";
import { useNavigate } from "react-router-dom";

// Component for displaying a single exam attempt row
const ExamAttemptRow = ({ attempt }) => {
  const formattedTime = useFormatTime(attempt.timeSpent);

  return (
    <Typography key={attempt._id} variant="body2">
      Recent attempt by{" "}
      {attempt.user?.name || attempt.user?.firstName
        ? attempt.user?.firstName + " " + attempt.user?.lastName
        : attempt.user?.username}
      {" - "} Exam:
      <b>{attempt.exam?.title}</b> (in {formattedTime})
    </Typography>
  );
};

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            opacity: value === index ? 1 : 0,
            transform: value === index ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [submissionsDialog, setSubmissionsDialog] = useState({
    open: false,
    type: "",
    id: "",
    title: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // RTK Query hooks
  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
  } = useGetAnalyticsQuery();

  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useGetUsersQuery();

  const { data: assignmentSubmissions, isLoading: submissionsLoading } =
    useGetAssignmentSubmissionsQuery(
      submissionsDialog.type === "assignment" ? submissionsDialog.id : null,
      { skip: submissionsDialog.type !== "assignment" || !submissionsDialog.id }
    );

  const { data: examAttempts, isLoading: attemptsLoading } =
    useGetExamAttemptsQuery(
      submissionsDialog.type === "exam" ? submissionsDialog.id : null,
      { skip: submissionsDialog.type !== "exam" || !submissionsDialog.id }
    );

  // Mutations
  const [updateUserRole] = useUpdateUserRoleMutation();
  const [deleteUser] = useDeleteUserMutation();

  const navigate = useNavigate();

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole({ userId, role: newRole }).unwrap();
      setEditDialog({ open: false, user: null });
      showSnackbar("User role updated successfully");
    } catch (error) {
      showSnackbar("Failed to update user role", "error");
      console.error(error);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId).unwrap();
      setDeleteDialog({ open: false, user: null });
      showSnackbar("User deleted successfully");
    } catch (error) {
      showSnackbar("Failed to delete user", "error");
      console.error(error);
    }
  };

  const handleViewSubmissions = (type, id, title) => {
    setSubmissionsDialog({ open: true, type, id, title });
  };

  if (analyticsLoading || usersLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const users = usersData?.users || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Admin Dashboard
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Manage your STUDY TOEIC WITH MR. S
          </Typography>
        </Box>

        {(analyticsError || usersError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading dashboard data. Please try refreshing.
          </Alert>
        )}

        <Paper
          sx={{
            width: "100%",
            mb: 3,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="admin dashboard tabs"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              mb: 1,
              backgroundColor: "rgba(255,255,255,0.7)",
              borderRadius: 3,
              p: 1,
              "& .MuiTabs-flexContainer": {
                gap: { xs: 1, sm: 1.5, md: 2 },
                justifyContent: { xs: "flex-start", md: "center" },
              },
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                minHeight: { xs: 60, sm: 64, md: 68 },
                minWidth: { xs: "90px", sm: "120px", md: "140px" },
                borderRadius: 2.5,
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                color: "rgba(0,0,0,0.7)",
                opacity: 0.85,
                my: 0.5,
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                "&:hover:not(.Mui-selected)": {
                  opacity: 1,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                },
                "& .MuiTab-iconWrapper": {
                  marginBottom: { xs: 0.5, md: 1 },
                  "& > svg": {
                    fontSize: { xs: 20, sm: 22, md: 24 },
                    transition: "all 0.3s ease",
                  },
                },
              },
              "& .MuiTabs-scrollButtons": {
                color: "#64748b",
                "&.Mui-disabled": { opacity: 0.3 },
              },
            }}
          >
            <Tab
              icon={<AnalyticsIcon />}
              label="Analytics"
              sx={{
                "&.Mui-selected": {
                  color: "white",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  boxShadow: "0 8px 16px rgba(59, 130, 246, 0.5)",
                  border: "1px solid rgba(59, 130, 246, 0.6)",
                },
                "&.Mui-selected .MuiTab-iconWrapper > svg": {
                  color: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
            <Tab
              icon={<Home />}
              label="Homepage"
              sx={{
                "&.Mui-selected": {
                  color: "white",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                  boxShadow: "0 8px 16px rgba(255, 152, 0, 0.5)",
                  border: "1px solid rgba(255, 152, 0, 0.6)",
                },
                "&.Mui-selected .MuiTab-iconWrapper > svg": {
                  color: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
            <Tab
              icon={<PeopleIcon />}
              label="Users"
              sx={{
                "&.Mui-selected": {
                  color: "white",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  boxShadow: "0 8px 16px rgba(16, 185, 129, 0.5)",
                  border: "1px solid rgba(16, 185, 129, 0.6)",
                },
                "&.Mui-selected .MuiTab-iconWrapper > svg": {
                  color: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
            {/* <Tab 
              icon={<AssignmentIcon />} 
              label="Assignments" 
              sx={{ 
                '&.Mui-selected': {
                  color: 'white',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  boxShadow: '0 8px 16px rgba(245, 158, 11, 0.5)',
                  border: '1px solid rgba(245, 158, 11, 0.6)'
                },
                '&.Mui-selected .MuiTab-iconWrapper > svg': {
                  color: 'white',
                  transform: 'scale(1.2)'
                }
              }}
            /> */}
            <Tab
              icon={<ExamIcon />}
              label="Exams"
              sx={{
                "&.Mui-selected": {
                  color: "white",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                  boxShadow: "0 8px 16px rgba(139, 92, 246, 0.5)",
                  border: "1px solid rgba(139, 92, 246, 0.6)",
                },
                "&.Mui-selected .MuiTab-iconWrapper > svg": {
                  color: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
            <Tab
              icon={<PartIcon />}
              label="Parts"
              sx={{
                "&.Mui-selected": {
                  color: "white",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                  boxShadow: "0 8px 16px rgba(6, 182, 212, 0.5)",
                  border: "1px solid rgba(6, 182, 212, 0.6)",
                },
                "&.Mui-selected .MuiTab-iconWrapper > svg": {
                  color: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
            <Tab
              icon={<MenuBook />}
              label="Exercises"
              sx={{
                "&.Mui-selected": {
                  color: "white",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  boxShadow: "0 8px 16px rgba(239, 68, 68, 0.5)",
                  border: "1px solid rgba(239, 68, 68, 0.6)",
                },
                "&.Mui-selected .MuiTab-iconWrapper > svg": {
                  color: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
          </Tabs>

          {/* Analytics Tab */}
          <TabPanel value={tabValue} index={0}>
            {analytics && (
              <Grid container spacing={3}>
                {/* User Statistics */}
                <Grid item xs={12} md={3}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <PeopleIcon sx={{ color: "white", fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          mb: 1,
                        }}
                      >
                        Total Users
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          mb: 2,
                        }}
                      >
                        {analytics?.users?.total || 0}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          fontWeight: 500,
                        }}
                      >
                        Students: {analytics?.users?.students || 0} | Admins:{" "}
                        {analytics?.users?.admins || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Content Statistics */}
                <Grid item xs={12} md={3}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <AssignmentIcon sx={{ color: "white", fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          mb: 2,
                        }}
                      >
                        Content Library
                      </Typography>
                      {/* <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#2196F3",
                            mb: 0.5,
                          }}
                        >
                          {analytics?.content?.assignments?.total || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", fontWeight: 500 }}
                        >
                          Assignments (
                          {analytics?.content?.assignments?.active || 0} active)
                        </Typography>
                      </Box> */}
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#00BCD4",
                            mb: 0.5,
                          }}
                        >
                          {analytics?.content?.exams?.total || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", fontWeight: 500 }}
                        >
                          Exams ({analytics?.content?.exams?.active || 0}{" "}
                          active)
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Activity Statistics */}
                <Grid item xs={12} md={3}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <AnalyticsIcon sx={{ color: "white", fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          mb: 2,
                        }}
                      >
                        Activity Statistics
                      </Typography>
                      {/* <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#2196F3",
                            mb: 0.5,
                          }}
                        >
                          {analytics?.activity?.assignmentSubmissions || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", fontWeight: 500 }}
                        >
                          Assignment Submissions
                        </Typography>
                      </Box> */}
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#4ade80",
                            mb: 0.5,
                          }}
                        >
                          {analytics?.activity?.examAttempts || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", fontWeight: 500 }}
                        >
                          Exam Attempts
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Performance Statistics */}
                <Grid item xs={12} md={3}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <TrendingUpIcon sx={{ color: "white", fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          mb: 2,
                        }}
                      >
                        Average Scores
                      </Typography>
                      {/* <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#2196F3",
                            mb: 0.5,
                          }}
                        >
                          {analytics?.performance?.avgAssignmentScore?.toFixed(
                            1
                          ) || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", fontWeight: 500 }}
                        >
                          Assignment Average
                        </Typography>
                      </Box> */}
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#fbbf24",
                            mb: 0.5,
                          }}
                        >
                          {analytics?.performance?.avgExamScore?.toFixed(1) ||
                            0}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", fontWeight: 500 }}
                        >
                          Exam Average
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Quick Links */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          mb: 2,
                        }}
                      >
                        Quick Links
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AssignmentIcon />}
                            onClick={() => navigate('/admin/exam-assignments')}
                            sx={{
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Exam Assignments
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ExerciseIcon />}
                            onClick={() => navigate('/admin/exercises')}
                            sx={{
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Exercises
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<BarChart />}
                            onClick={() => navigate('/admin/reports')}
                            sx={{
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Reports
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Recent Activity */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ color: "#1976d2", fontWeight: "bold" }}
                      >
                        Analytics Dashboard
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        Recent Activity
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1">
                            Recent Exam Attempts
                          </Typography>
                          {analytics.activity.recentExamAttempts?.map(
                            (attempt) => (
                              <ExamAttemptRow
                                key={attempt._id}
                                attempt={attempt}
                              />
                            )
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Homepage Tab */}
          <TabPanel value={tabValue} index={1}>
            <HomepageManagement />
          </TabPanel>

          {/* Users Tab */}
          <TabPanel value={tabValue} index={2}>
            <UserManagement />
          </TabPanel>

          {/* <TabPanel value={tabValue} index={3}>
          <AssignmentManagement />
        </TabPanel> */}

          <TabPanel value={tabValue} index={3}>
            <ExamManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <PartManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <ExerciseManagement />
          </TabPanel>
        </Paper>

        {/* Submissions/Attempts Dialog */}
        <Dialog
          open={submissionsDialog.open}
          onClose={() =>
            setSubmissionsDialog({ open: false, type: "", id: "", title: "" })
          }
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {submissionsDialog.type === "assignment"
              ? "Assignment Submissions"
              : "Exam Attempts"}
            : {submissionsDialog.title}
          </DialogTitle>
          <DialogContent>
            {submissionsLoading || attemptsLoading ? (
              <CircularProgress />
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Attempt</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Time Spent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissionsDialog.type === "assignment"
                      ? assignmentSubmissions?.submissions?.map(
                          (submission) => (
                            <TableRow key={submission._id}>
                              <TableCell>{submission.student?.name}</TableCell>
                              <TableCell>{submission.score}%</TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    submission.isPassed ? "Passed" : "Failed"
                                  }
                                  color={
                                    submission.isPassed ? "success" : "error"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{submission.attemptNumber}</TableCell>
                              <TableCell>
                                {new Date(
                                  submission.submittedAt
                                ).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {Math.round(submission.timeSpent / 60)} min
                              </TableCell>
                            </TableRow>
                          )
                        )
                      : examAttempts?.attempts?.map((attempt) => (
                          <TableRow key={attempt._id}>
                            <TableCell>{attempt.student?.name}</TableCell>
                            <TableCell>{attempt.overallScore}%</TableCell>
                            <TableCell>
                              <Chip
                                label={attempt.isPassed ? "Passed" : "Failed"}
                                color={attempt.isPassed ? "success" : "error"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{attempt.attemptNumber}</TableCell>
                            <TableCell>
                              {new Date(attempt.submittedAt).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {Math.round(attempt.totalTimeSpent / 60)} min
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setSubmissionsDialog({
                  open: false,
                  type: "",
                  id: "",
                  title: "",
                })
              }
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
